import type { PayloadAction } from "@reduxjs/toolkit";
import { createSlice } from "@reduxjs/toolkit";
import type { Patch } from "@freightsimple/shared/types";
import type { UUID } from "@/Shared/types";
import {
  lineItemsEmpty,
  lineItemFromSavedCommodity,
} from "@/Shared/LineItems/Helpers/lineItemsEmpty";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import type { Broker } from "@freightsimple/generated-dashboard-openapi-client/models/Broker";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { PreBookingShipment } from "@freightsimple/generated-dashboard-openapi-client/models/PreBookingShipment";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import { LoadingCapabilitiesAvailability } from "@freightsimple/generated-dashboard-openapi-client/models/LoadingCapabilitiesAvailability";
import { LocationContext } from "@freightsimple/generated-dashboard-openapi-client/models/LocationContext";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import { bookShipmentLoadDraftState } from "@/Slices/Helpers/bookShipmentLoadDraftState";
import { bookShipmentLoadPreBookingState } from "@/Features/BookShipment/Helpers/bookShipmentLoadPreBookingState";
import { bookShipmentResetStateToModify } from "@/Features/BookShipment/Helpers/bookShipmentResetStateToModify";
import {
  bookShipmentEmptyLocationInfo,
  initialBookShipmentSliceState,
} from "@/Features/BookShipment/Helpers/bookShipmentEmptyState";
import {
  bookShipmentDuplicateLineItem,
  bookShipmentRearrangeLengthWidth,
  bookShipmentUpdateLineItem,
} from "@/Features/BookShipment/Helpers/bookShipmentLineItemReducers";
import {
  bookShipmentLocationWithRecommendedAccessorials,
  bookShipmentResetState,
} from "@/Features/BookShipment/Helpers/bookShipmentLocationReducers";
import { bookShipmentDropHiddenDeadline } from "@/Features/BookShipment/Helpers/bookShipmentDropHiddenDeadline";
import type {
  BookShipmentError,
  BookShipmentShipmentType,
  ShipmentDraftState,
} from "@/Shared/Types/bookShipmentSliceTypes";

export const bookShipmentSlice = createSlice({
  name: "bookShipment",
  initialState: initialBookShipmentSliceState(),
  reducers: {
    resetShipment(
      _state,
      action: PayloadAction<{
        savedLocations: Array<SavedLocation>;
        defaultPickupLocationId: UUID | undefined;
        defaultDeliveryLocationId: UUID | undefined;
      }>,
    ) {
      return bookShipmentResetState(action.payload);
    },

    loadFromShipment(
      state,
      action: PayloadAction<{
        shipment: PreBookingShipment;
        savedLocations: Array<SavedLocation>;
      }>,
    ) {
      return bookShipmentLoadPreBookingState({
        state: state,
        shipment: action.payload.shipment,
        savedLocations: action.payload.savedLocations,
      });
    },

    loadFromShipmentToModify(
      _,
      action: PayloadAction<{
        shipment: BookShipmentShipmentType | PreBookingShipment;
        savedLocations: Array<SavedLocation>;
      }>,
    ) {
      return bookShipmentResetStateToModify({
        shipment: action.payload.shipment,
        savedLocations: action.payload.savedLocations,
      });
    },

    loadFromDraft(
      _,
      action: PayloadAction<{
        draft: ShipmentDraftState;
        savedLocations: Array<SavedLocation>;
      }>,
    ) {
      return bookShipmentLoadDraftState({
        draft: action.payload.draft,
        savedLocations: action.payload.savedLocations,
      });
    },

    setShipmentLoading(state, action: PayloadAction<boolean>) {
      state.loadingShipment = action.payload;
    },

    setShipmentError(
      state,
      action: PayloadAction<BookShipmentError | undefined>,
    ) {
      state.shipmentError = action.payload;
    },

    setPickupDate(state, action: PayloadAction<string>) {
      state.shipment.pickupDate = action.payload;
    },

    addLineItem(state) {
      state.shipment.lineItems.push(lineItemsEmpty());
    },

    duplicateLineItem(state, action: PayloadAction<UUID>) {
      bookShipmentDuplicateLineItem(state.shipment, action.payload);
    },

    addLineItemFromSavedCommodity(
      state,
      action: PayloadAction<SavedCommodity>,
    ) {
      state.shipment.lineItems.push(lineItemFromSavedCommodity(action.payload));
    },

    updateLineItem(
      state,
      action: PayloadAction<{
        lineItemId: UUID;
        value: Patch<LineItem>;
      }>,
    ) {
      bookShipmentUpdateLineItem(
        state.shipment,
        action.payload.lineItemId,
        action.payload.value,
      );
    },

    removeLineItem(state, action: PayloadAction<UUID>) {
      state.shipment.lineItems = state.shipment.lineItems.filter((lineItem) => {
        return lineItem.lineItemId !== action.payload;
      });
    },

    rearrangeLengthWidth(state, action: PayloadAction<UUID>) {
      bookShipmentRearrangeLengthWidth(state.shipment, action.payload);
    },

    newPickupLocation(state) {
      state.shipment.pickupLocation = bookShipmentEmptyLocationInfo();
      state.shipment.pickupLocationHasDock =
        LoadingCapabilitiesAvailability.Unknown;
      state.shipment.pickupLocationHasForklift =
        LoadingCapabilitiesAvailability.Unknown;
    },

    newDeliveryLocation(state) {
      state.shipment.deliveryLocation = bookShipmentEmptyLocationInfo();
      state.shipment.deliveryLocationHasDock =
        LoadingCapabilitiesAvailability.Unknown;
      state.shipment.deliveryLocationHasForklift =
        LoadingCapabilitiesAvailability.Unknown;
    },

    setPickupLocation(state, action: PayloadAction<AddLocationLocationType>) {
      state.shipment.pickupLocation =
        bookShipmentLocationWithRecommendedAccessorials(
          state.shipment.pickupLocation,
          action.payload,
        );

      bookShipmentDropHiddenDeadline(state.shipment, LocationContext.Pickup);
    },

    setDeliveryLocation(state, action: PayloadAction<AddLocationLocationType>) {
      state.shipment.deliveryLocation =
        bookShipmentLocationWithRecommendedAccessorials(
          state.shipment.deliveryLocation,
          action.payload,
        );

      bookShipmentDropHiddenDeadline(state.shipment, LocationContext.Delivery);
    },

    setCreditCardBrand(state, action: PayloadAction<string>) {
      state.creditCardBrand = action.payload;
    },

    setSelectedPickupSavedLocationId(
      state,
      action: PayloadAction<UUID | undefined>,
    ) {
      state.selectedPickupSavedLocationId = action.payload;
    },

    setSelectedDeliverySavedLocationId(
      state,
      action: PayloadAction<UUID | undefined>,
    ) {
      state.selectedDeliverySavedLocationId = action.payload;
    },

    setEnteringNewPickup(state, action: PayloadAction<boolean>) {
      state.enteringNewPickup = action.payload;
    },

    setEnteringNewDelivery(state, action: PayloadAction<boolean>) {
      state.enteringNewDelivery = action.payload;
    },

    updateBroker(state, action: PayloadAction<Broker>) {
      state.shipment.broker = action.payload;
    },

    updateStripePaymentMethod(state, action: PayloadAction<string>) {
      state.shipment.stripePaymentMethodId = action.payload;
    },

    setCurrentScreenIsDirty(state) {
      state.isCurrentScreenDirty = true;
    },

    setCurrentScreenIsNotDirty(state) {
      state.isCurrentScreenDirty = false;
    },

    markFlowItemCompleted(state, action: PayloadAction<string>) {
      const items = [...state.completedFlowItems];
      if (!items.includes(action.payload)) {
        items.push(action.payload);
      }
      state.completedFlowItems = items;
    },

    setPickupReferenceNumber(state, action: PayloadAction<string>) {
      state.shipment.pickupReferenceNumber = action.payload;
    },

    setDeliveryReferenceNumber(state, action: PayloadAction<string>) {
      state.shipment.deliveryReferenceNumber = action.payload;
    },

    setDeliveryAmazonReferenceId(state, action: PayloadAction<string>) {
      state.shipment.deliveryAmazonReferenceId = action.payload;
    },

    setDeliveryAmazonShipmentId(state, action: PayloadAction<string>) {
      state.shipment.deliveryAmazonShipmentId = action.payload;
    },

    setPickupBoothNumber(state, action: PayloadAction<string>) {
      state.shipment.pickupBoothNumber = action.payload;
    },

    setDeliveryBoothNumber(state, action: PayloadAction<string>) {
      state.shipment.deliveryBoothNumber = action.payload;
    },

    setMakePickupLocationAFavourite(state, action: PayloadAction<boolean>) {
      state.makePickupLocationAFavourite = action.payload;
    },

    setMakeDeliveryLocationAFavourite(state, action: PayloadAction<boolean>) {
      state.makeDeliveryLocationAFavourite = action.payload;
    },

    onAddInsuranceToShipmentChanged(state, action: PayloadAction<boolean>) {
      const addInsuranceToShipment = action.payload;
      state.shipment.addInsuranceToShipment = addInsuranceToShipment;

      if (addInsuranceToShipment === false) {
        state.shipment.stateOfGoods = undefined;
      }
    },

    onSetInsuranceAmount(state, action: PayloadAction<number>) {
      state.shipment.insuranceAmount = action.payload;
    },

    onSetInsuranceCurrency(state, action: PayloadAction<Currency>) {
      state.shipment.insuranceCurrency = action.payload;
    },

    setStateOfGoods(state, action: PayloadAction<StateOfGoods>) {
      state.shipment.stateOfGoods = action.payload;
    },

    setBranchId(state, action: PayloadAction<string | undefined>) {
      state.shipment.branchId = action.payload;
    },

    setDeliveryDeadline(state, action: PayloadAction<string | undefined>) {
      state.shipment.deliveryDeadline = action.payload;
    },

    setPickupDeadline(state, action: PayloadAction<string | undefined>) {
      state.shipment.pickupDeadline = action.payload;
    },

    setHasPickupDeadline(state, action: PayloadAction<boolean>) {
      if (!action.payload) {
        state.shipment.pickupDeadline = undefined;
      }
    },

    setHasDeliveryDeadline(state, action: PayloadAction<boolean>) {
      if (!action.payload) {
        state.shipment.deliveryDeadline = undefined;
      }
    },

    setPickupLocationHasDock(
      state,
      action: PayloadAction<LoadingCapabilitiesAvailability | undefined>,
    ) {
      state.shipment.pickupLocationHasDock = action.payload;
    },

    setPickupLocationHasForklift(
      state,
      action: PayloadAction<LoadingCapabilitiesAvailability | undefined>,
    ) {
      state.shipment.pickupLocationHasForklift = action.payload;
    },

    setDeliveryLocationHasDock(
      state,
      action: PayloadAction<LoadingCapabilitiesAvailability | undefined>,
    ) {
      state.shipment.deliveryLocationHasDock = action.payload;
    },

    setDeliveryLocationHasForklift(
      state,
      action: PayloadAction<LoadingCapabilitiesAvailability | undefined>,
    ) {
      state.shipment.deliveryLocationHasForklift = action.payload;
    },

    linkShipmentDraft(state, action: PayloadAction<UUID>) {
      if (state.draft) {
        state.draft.shipmentId = action.payload;
      }
    },
  },
});

export const {
  loadFromShipment,
  loadFromShipmentToModify,
  loadFromDraft,
  setShipmentLoading,
  setShipmentError,
  resetShipment,
  setPickupDate,
  addLineItem,
  duplicateLineItem,
  addLineItemFromSavedCommodity,
  updateLineItem,
  removeLineItem,
  newPickupLocation,
  newDeliveryLocation,
  rearrangeLengthWidth,
  setPickupLocation,
  setDeliveryLocation,
  setCreditCardBrand,
  setSelectedPickupSavedLocationId,
  setSelectedDeliverySavedLocationId,
  setEnteringNewPickup,
  setEnteringNewDelivery,
  updateBroker,
  updateStripePaymentMethod,
  setCurrentScreenIsDirty,
  setCurrentScreenIsNotDirty,
  markFlowItemCompleted,
  setPickupReferenceNumber,
  setDeliveryReferenceNumber,
  setDeliveryAmazonReferenceId,
  setDeliveryAmazonShipmentId,
  setMakePickupLocationAFavourite,
  setMakeDeliveryLocationAFavourite,
  onAddInsuranceToShipmentChanged,
  onSetInsuranceAmount,
  onSetInsuranceCurrency,
  setStateOfGoods,
  setBranchId,
  setDeliveryDeadline,
  setHasDeliveryDeadline,
  setPickupDeadline,
  setHasPickupDeadline,
  setPickupLocationHasDock,
  setPickupLocationHasForklift,
  setDeliveryLocationHasDock,
  setDeliveryLocationHasForklift,
  linkShipmentDraft,
} = bookShipmentSlice.actions;
